<template>
    <div class="bond-v2-page">
        <a href="#bond-v2-main-content" class="bond-v2-skip-link">Skip to main content</a>
        <SignupEnforcementDialog
            v-model="showSignupDialog"
            type="signup"
            :title="signupDialogContent.title"
            :message="signupDialogContent.message"
            :cta-name="signupDialogContent.ctaName"
        />
        <BondSignupGateModal
            :model-value="bondGateShowPopup"
            title="Sign Up to Continue Exploring"
            message="Sign up to unlock full access to detailed bond information, cashflow schedules, and secure investment options."
            :signup-url="bondGateSignupUrl"
            @update:model-value="handleBondGateUpdate"
        />
        <div v-if="showInterestModal" class="interest-modal-overlay" @click.self="closeInterestModal">
            <div
                id="interest-modal-dialog"
                ref="interestModalDialogRef"
                class="interest-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="interest-modal-title"
                tabindex="-1"
                @keydown="handleInterestModalKeydown"
            >
                <h2 id="interest-modal-title" class="interest-modal__title">Express your interest</h2>
                <p v-if="bondListing" class="interest-modal__bond-label">
                    ISIN: {{ bondListing?.instrument?.isin || isin }} ({{ bondListing?.instrument?.issuerName || formattedBondName }})
                </p>
                <form class="interest-modal__form" @submit.prevent="handleInterestSubmit">
                    <p class="interest-modal__instruction">* indicates required fields.</p>
                    <div class="interest-modal__field">
                        <label for="interest-name">Name *</label>
                        <input
                            id="interest-name"
                            v-model.trim="interestName"
                            type="text"
                            placeholder="Your name"
                            class="interest-modal__input"
                            autocomplete="name"
                            aria-required="true"
                            required
                        />
                    </div>
                    <div class="interest-modal__field">
                        <label for="interest-phone">WhatsApp number *</label>
                        <div class="interest-modal__phone-row">
                            <span class="interest-modal__phone-prefix">+91 -</span>
                            <input
                                id="interest-phone"
                                :value="interestPhoneDigits"
                                type="tel"
                                inputmode="numeric"
                                autocomplete="tel-national"
                                maxlength="10"
                                placeholder="Enter a 10-digit mobile number (e.g., 9876543210)"
                                aria-label="Enter a 10-digit mobile number (e.g., 9876543210)"
                                class="interest-modal__input interest-modal__input--phone"
                                aria-required="true"
                                required
                                @input="onInterestPhoneInput"
                            />
                        </div>
                    </div>
                    <div class="interest-modal__field">
                        <label for="interest-investment-amount">Investment Amount (₹) *</label>
                        <input
                            id="interest-investment-amount"
                            :value="interestInvestmentAmountDisplay"
                            type="text"
                            inputmode="numeric"
                            placeholder="Enter investment amount, e.g. ₹5,00,000"
                            class="interest-modal__input"
                            aria-required="true"
                            required
                            @input="onInterestInvestmentAmountInput"
                        />
                    </div>
                    <p v-if="interestError" class="interest-modal__error" role="alert">{{ interestError }}</p>
                    <button type="submit" class="interest-modal__submit" :disabled="interestSubmitting || !canSubmitInterest">
                        {{ interestSubmitting ? 'Submitting...' : 'Get in touch' }}
                    </button>
                </form>
                <button type="button" class="interest-modal__close" aria-label="Close" @click="closeInterestModal">×</button>
            </div>
        </div>
            <div class="bond-v2-page__container">
                <div ref="stickyScopeRef" class="bond-v2-sticky-scope">
                    <div
                        ref="stickyHeaderRef"
                        class="bond-v2-sticky-header"
                        :class="{ 'bond-v2-sticky-header--fixed': isBondHeaderFixed }"
                        :style="bondHeaderStyle"
                    >
                        <div class="bond-v2-back">
                            <button type="button" aria-label="Go back" @click="goBack">
                                <img src="@/assets/icons/bond-details-v2/back.svg" alt="" aria-hidden="true" />
                            </button>
                        </div>

                        <BondV2Header v-if="!isLoading && bondListing" />
                        <section v-else-if="isLoading" class="bond-v2-header-skeleton">
                            <div class="bond-v2-skeleton-chip bond-v2-skeleton-shimmer"></div>
                            <div class="bond-v2-skeleton-chip bond-v2-skeleton-chip--short bond-v2-skeleton-shimmer"></div>
                            <div class="bond-v2-skeleton-chip bond-v2-skeleton-chip--right bond-v2-skeleton-shimmer"></div>
                            <div class="bond-v2-header-skeleton__issuer">
                                <div class="bond-v2-skeleton-logo bond-v2-skeleton-shimmer"></div>
                                <div class="bond-v2-header-skeleton__copy">
                                    <div class="bond-v2-skeleton-line bond-v2-skeleton-line--title bond-v2-skeleton-shimmer"></div>
                                    <div class="bond-v2-skeleton-line bond-v2-skeleton-line--small bond-v2-skeleton-shimmer"></div>
                                </div>
                            </div>
                        </section>
                    </div>

                    <div
                        v-if="isLoading || bondListing"
                        ref="detailRegionRef"
                        class="bond-v2-detail-region"
                        :style="detailRegionStyle"
                        @click.capture="scheduleStickyRefresh"
                    >
                        <div class="bond-v2-layout">
                            <div id="bond-v2-main-content" class="bond-v2-main" tabindex="-1">
                                <template v-if="isLoading">
                                    <section class="bond-v2-overview-box bond-v2-overview-box--skeleton">
                                        <div class="bond-v2-metrics-skeleton">
                                            <div v-for="item in 6" :key="item" class="bond-v2-metric-skeleton-card">
                                                <div class="bond-v2-skeleton-icon bond-v2-skeleton-shimmer"></div>
                                                <div class="bond-v2-skeleton-line bond-v2-skeleton-line--metric bond-v2-skeleton-shimmer"></div>
                                                <div class="bond-v2-skeleton-line bond-v2-skeleton-line--label bond-v2-skeleton-shimmer"></div>
                                            </div>
                                        </div>
                                        <div class="bond-v2-overview-summary-skeleton">
                                            <div class="bond-v2-skeleton-line bond-v2-skeleton-line--wide bond-v2-skeleton-shimmer"></div>
                                            <div class="bond-v2-skeleton-line bond-v2-skeleton-line--wide bond-v2-skeleton-shimmer"></div>
                                            <div class="bond-v2-yield-skeleton-row">
                                                <div class="bond-v2-yield-skeleton-card bond-v2-skeleton-shimmer"></div>
                                                <div class="bond-v2-yield-skeleton-card bond-v2-skeleton-shimmer"></div>
                                            </div>
                                        </div>
                                    </section>

                                    <section class="bond-v2-cashflow-skeleton">
                                        <div class="bond-v2-skeleton-line bond-v2-skeleton-line--section bond-v2-skeleton-shimmer"></div>
                                        <div class="bond-v2-cashflow-summary-skeleton">
                                            <div v-for="item in 3" :key="item" class="bond-v2-cashflow-summary-card bond-v2-skeleton-shimmer"></div>
                                        </div>
                                        <div class="bond-v2-table-skeleton">
                                            <div v-for="item in 4" :key="item" class="bond-v2-table-skeleton__row bond-v2-skeleton-shimmer"></div>
                                        </div>
                                    </section>
                                </template>

                                <template v-else-if="bondListing">
                                    <section class="bond-v2-overview-box">
                                        <BondV2MetricGrid />
                                        <BondV2IssuerOverview :show-subscription="availableForSale" />
                                    </section>
                                    <BondV2Cashflow
                                        v-if="availableForSale"
                                        :units="sharedUnits"
                                        :min-units="minUnits"
                                        :max-units="maxUnits"
                                        :lot-size="lotSize"
                                        @signup-required="handleSignupRequired"
                                        @units-changed="handleUnitsChanged"
                                    />
                                </template>
                                <template v-if="!isLoading">
                                    <BondV2IssuerGlance @signup-required="handleSignupRequired" />
                                    <BondV2StrengthsRisks @signup-required="handleSignupRequired" />
                                    <BondV2CreditRatingIntelligence @signup-required="handleSignupRequired" />
                                </template>
                            </div>

                            <div ref="sidebarRef" class="bond-v2-sidebar">
                                <div
                                    ref="sidebarStickyRef"
                                    class="bond-v2-sidebar__sticky"
                                    :class="{ 'bond-v2-sidebar__sticky--fixed': isBondSidebarFixed }"
                                    :style="bondSidebarStyle"
                                >
                                    <BondV2InvestPanel
                                        ref="investPanelRef"
                                        v-if="!isLoading && bondListing"
                                        :units="sharedUnits"
                                        :min-units="minUnits"
                                        :max-units="maxUnits"
                                        :lot-size="lotSize"
                                        :invest-button-label="investButtonLabel"
                                        :lead-mode="!availableForSale"
                                        @invest="handleInvest"
                                        @view-cashflow="handleViewCashflow"
                                        @units-changed="handleUnitsChanged"
                                    />
                                    <section v-else-if="isLoading" class="bond-v2-invest-skeleton">
                                        <div class="bond-v2-skeleton-line bond-v2-skeleton-line--center bond-v2-skeleton-shimmer"></div>
                                        <div class="bond-v2-invest-skeleton__unit bond-v2-skeleton-shimmer"></div>
                                        <div class="bond-v2-invest-skeleton__buttons">
                                            <div v-for="item in 3" :key="item" class="bond-v2-invest-skeleton__button bond-v2-skeleton-shimmer"></div>
                                        </div>
                                        <div class="bond-v2-invest-skeleton__totals">
                                            <div v-for="item in 4" :key="item" class="bond-v2-table-skeleton__row bond-v2-skeleton-shimmer"></div>
                                        </div>
                                    </section>
                                    <section
                                        v-if="!isLoading && !availableForSale && (recommendationsPending || hasInactiveRecommendations)"
                                        class="bond-v2-recommendations"
                                        aria-live="polite"
                                        aria-labelledby="similar-bonds-heading"
                                    >
                                        <h2 id="similar-bonds-heading" class="bond-v2-recommendations__title">Similar bonds</h2>
                                        <p v-if="recommendationsPending" class="bond-v2-recommendations__loading">Finding best matches...</p>
                                        <div v-else class="bond-v2-recommendations__cards">
                                            <BondProductCard
                                                v-for="(recommendation, index) in recommendationCards"
                                                :key="`${recommendation.isin || recommendation.bondId || 'recommended'}-${index}`"
                                                v-bind="recommendation"
                                            />
                                        </div>
                                    </section>
                                    <BondV2RegulatoryAssurance
                                        v-if="!isLoading && bondV2Data.regulatoryAssurance"
                                        @signup-required="handleSignupRequired"
                                    />
                                    <section v-else-if="isLoading" class="bond-v2-documents-skeleton">
                                        <div class="bond-v2-skeleton-line bond-v2-skeleton-line--docs-title bond-v2-skeleton-shimmer"></div>
                                        <div class="bond-v2-documents-skeleton__rows">
                                            <div v-for="item in 5" :key="item" class="bond-v2-documents-skeleton__row">
                                                <div class="bond-v2-skeleton-line bond-v2-skeleton-line--docs-row bond-v2-skeleton-shimmer"></div>
                                                <div class="bond-v2-skeleton-doc-icon bond-v2-skeleton-shimmer"></div>
                                            </div>
                                        </div>
                                    </section>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <section v-if="showEmptyState" class="bond-v2-empty-state" role="status" aria-live="polite">
                    <div class="bond-v2-empty-state__content">
                        <div class="bond-v2-empty-state__icon" aria-hidden="true">
                            <VIcon size="28">mdi-alert-circle-outline</VIcon>
                        </div>
                        <h2 class="bond-v2-empty-state__title">{{ emptyStateTitle }}</h2>
                        <div class="bond-v2-empty-state__actions">
                            <CommonButton
                                text="Try Again"
                                class="bond-v2-empty-state__button"
                                width="124px"
                                height="46px"
                                color="#0027af"
                                :action="loadBondDetails"
                            />
                            <button type="button" class="bond-v2-empty-state__link" @click="goBack">
                                Go Back
                            </button>
                        </div>
                    </div>
                </section>

                <div ref="stickyEndRef" class="bond-v2-sticky-end" aria-hidden="true"></div>

                <BondV2Blogs v-if="!isLoading && bondListing" />

                <BondV2CashflowDialog
                    v-model="showCashflowDialog"
                    :units="sharedUnits"
                    :min-units="minUnits"
                    :max-units="maxUnits"
                    :lot-size="lotSize"
                    @units-changed="handleUnitsChanged"
                />
            </div>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import CommonButton from '@/components/common/button.vue'
import BondV2Header from '@/components/bondsV2/BondV2Header.vue'
import BondV2MetricGrid from '@/components/bondsV2/BondV2MetricGrid.vue'
import BondV2IssuerOverview from '@/components/bondsV2/BondV2IssuerOverview.vue'
import BondV2Cashflow from '@/components/bondsV2/BondV2Cashflow.vue'
import BondV2IssuerGlance from '@/components/bondsV2/BondV2IssuerGlance.vue'
import BondV2StrengthsRisks from '@/components/bondsV2/BondV2StrengthsRisks.vue'
import BondV2CreditRatingIntelligence from '@/components/bondsV2/BondV2CreditRatingIntelligence.vue'
import BondV2Blogs from '@/components/bondsV2/BondV2Blogs.vue'
import BondV2InvestPanel from '@/components/bondsV2/BondV2InvestPanel.vue'
import BondV2CashflowDialog from '@/components/bondsV2/BondV2CashflowDialog.vue'
import BondV2RegulatoryAssurance from '@/components/bondsV2/BondV2RegulatoryAssurance.vue'
import BondProductCard, { type Props as BondProductCardProps } from '@/components/bonds/BondProductCard.vue'
import BondSignupGateModal from '@/components/common/BondSignupGateModal.vue'
import SignupEnforcementDialog from '@/components/common/SignupEnforcementDialog.vue'
import bondsService from '@/service/bondsService'
import type { BondsDetailsIssuerFactsData, BondsListingDetail } from '@/proto/bonds'
import { provideBondV2Data } from '@/components/bondsV2/bondV2Context'
import { mapBondListingToBondV2Data } from '@/components/bondsV2/bondV2Mapper'
import { normalizeBondListing, type NormalizedBondListing } from '@/utils/normalizations'
import {
    CREDIT_RATING_ORDER,
    getBondMinInvestmentAmount,
    getBondPricePerUnit,
    getCreditRatingIndex,
    getPrimaryCreditRatingToken,
    parseBondSlug,
    slugifyBondName
} from '@/utils/common'
import { useUtmTracking } from '@/composables/useUtmTracking'
import { useBondSignupGate } from '@/composables/useBondSignupGate'
import analytics, { EVENTS } from '~/services/analytics'
import { usePageView } from '~/composables/usePageView'

function bondContext(listing: any) {
  const perUnit = listing?.cashflow?.totalPurchaseValueForYieldOffered ?? listing?.minInvestmentPerUnit
  const minUnits = Math.max(1, listing?.instrument?.minUnitsToSell ?? 1)
  return {
    isin:                 listing?.instrument?.isin,
    bond_name:            listing?.instrument?.name,
    issuer_name:          listing?.instrument?.issuerName,
    listed_yield:         listing?.listedYield,
    min_investment:       (perUnit != null && perUnit > 0) ? perUnit * minUnits : undefined,
  }
}

interface BondDetailsDataPayload {
    listing: BondsListingDetail | null
    issuerFactsData?: BondsDetailsIssuerFactsData
    tradeDate?: string
    availableForSale: boolean
}

interface SignupDialogContent {
    title: string
    message: string
    ctaName: string
}

defineOptions({ name: 'PublicBondDetailsDataV2Page' })

const route = useRoute()
const router = useRouter()

usePageView({ page_type: 'detail', product: 'bonds' });
const { buildRedirectUrl } = useUtmTracking()
const { investorUrl } = useInvestorPlatform()
const {
    showPopup: bondGateShowPopup,
    incrementVisit,
    setupBroadcast,
    startPopupTimer,
    openClosablePopup,
    resetAfterCooldown
} = useBondSignupGate()
const SITE_URL = 'https://www.equirize.com'

const slug = computed(() => String(route.params.slug ?? ''))
const parsedSlug = computed(() => parseBondSlug(slug.value))
const isin = computed(() => parsedSlug.value.isin?.trim() || '')
const formattedBondName = computed(() => {
    const raw = parsedSlug.value.bondName || ''
    if (!raw) return 'Bond'

    return raw
        .split('-')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ') || 'Bond'
})

const handleBondGateUpdate = (value: boolean) => {
    bondGateShowPopup.value = value
}

const defaultCashflowSignupContent: SignupDialogContent = {
    title: 'Sign Up to View Cashflow',
    message: 'Sign up to access detailed cashflow schedules and see your potential returns.',
    ctaName: 'signup-cashflow-bond-details'
}

const showCashflowDialog = ref(false)
const showSignupDialog = ref(false)
const signupDialogContent = ref<SignupDialogContent>(defaultCashflowSignupContent)
const sharedUnits = ref(1)
const isLeavingPage = ref(false)
const lastLoadedBondDetailsData = ref<BondDetailsDataPayload | null>(null)
const showInterestModal = ref(false)
const interestName = ref('')
const interestPhoneDigits = ref('')
const interestInvestmentAmount = ref('')
const interestSubmitting = ref(false)
const interestError = ref('')
const interestModalDialogRef = ref<HTMLElement | null>(null)
let interestModalTriggerElement: HTMLElement | null = null
const stickyScopeRef = ref<HTMLElement | null>(null)
const stickyHeaderRef = ref<HTMLElement | null>(null)
const detailRegionRef = ref<HTMLElement | null>(null)
const stickyEndRef = ref<HTMLElement | null>(null)
const sidebarRef = ref<HTMLElement | null>(null)
const sidebarStickyRef = ref<HTMLElement | null>(null)
const investPanelRef = ref<InstanceType<typeof BondV2InvestPanel> | null>(null)
const isBondHeaderFixed = ref(false)
const isBondSidebarFixed = ref(false)
const stickyHeaderHeight = ref(0)
const bondHeaderStyle = ref<Record<string, string>>({})
const bondSidebarStyle = ref<Record<string, string>>({})
let stickyFrame = 0
let stickyResizeObserver: ResizeObserver | null = null
let lastHeights = new WeakMap<Element, number>()
const activeTimeouts = ref<any[]>([])
const WHATSAPP_NUMBER = '918712498971'
const IN_MOBILE_10 = /^[6-9]\d{9}$/
const RECOMMENDATION_FETCH_LIMIT = 120
const RECOMMENDATION_COUNT = 3

const todayDate = () => new Date().toISOString().split('T')[0]

const normalizePath = (path: string) => {
    let normalized = path
    while (normalized.endsWith('/')) normalized = normalized.slice(0, -1)
    return normalized || '/'
}

const getCanonicalBondPath = (listing: BondsListingDetail | null | undefined) => {
    const listingIsin = listing?.instrument?.isin?.trim() || isin.value
    const issuerName = listing?.instrument?.issuerName || listing?.instrument?.name || formattedBondName.value
    const issuerSlug = slugifyBondName(issuerName)

    return listingIsin ? `/products/bonds/${listingIsin}-${issuerSlug}` : ''
}

const { data: bondDetailsData, pending, refresh } = await useAsyncData(
    () => `bond-details-data-${slug.value}`,
    async (): Promise<BondDetailsDataPayload> => {
        if (!isin.value) {
            throw createError({ statusCode: 400, statusMessage: 'Invalid bond link' })
        }

        const response = await bondsService.getBondDetailsData(isin.value) as any
        const listing = response?.listing ?? null

        if (!listing) {
            throw createError({ statusCode: 404, statusMessage: 'Bond details unavailable' })
        }

        return {
            listing,
            issuerFactsData: response.issuerFactsData,
            tradeDate: response.tradeDate || todayDate(),
            availableForSale: response.available_for_sale !== false
        }
    },
    {
        server: true,
        watch: [slug],
        default: () => ({
            listing: null,
            issuerFactsData: undefined,
            tradeDate: todayDate(),
            availableForSale: true
        })
    }
)

const canonicalBondPath = computed(() => getCanonicalBondPath(bondDetailsData.value?.listing))
if (canonicalBondPath.value && normalizePath(route.path) !== normalizePath(canonicalBondPath.value)) {
    await navigateTo(canonicalBondPath.value, {
        redirectCode: 301,
        replace: true
    })
}

onMounted(() => {
    if (bondDetailsData.value?.listing) {
        lastLoadedBondDetailsData.value = bondDetailsData.value
        analytics.track(EVENTS.PRODUCT_VIEWED, {
            product:   'bonds',
            page_type: 'detail',
            user_type: 'anonymous',
            ...bondContext(bondDetailsData.value.listing),
        })
    }
})

const visibleBondDetailsData = computed(() => {
    if (bondDetailsData.value?.listing) return bondDetailsData.value
    if (isLeavingPage.value && lastLoadedBondDetailsData.value?.listing) return lastLoadedBondDetailsData.value

    return bondDetailsData.value
})

const bondListing = computed(() => visibleBondDetailsData.value?.listing ?? null)
const issuerFactsData = computed(() => visibleBondDetailsData.value?.issuerFactsData)
const tradeDate = computed(() => visibleBondDetailsData.value?.tradeDate)
const availableForSale = computed(() => visibleBondDetailsData.value?.availableForSale !== false)
const isUsingLeavingPageSnapshot = computed(() => isLeavingPage.value && Boolean(lastLoadedBondDetailsData.value?.listing))
const isLoading = computed(() => pending.value && !isUsingLeavingPageSnapshot.value)
const showEmptyState = computed(() => !isLoading.value && !bondListing.value && !isLeavingPage.value)
const hasInactiveRecommendations = computed(() => !availableForSale.value && recommendationCards.value.length > 0)
const lotSize = computed(() => {
    const size = Math.floor(Number(bondListing.value?.lotSize) || 1)
    return Math.max(1, size)
})
const roundUpToLotSize = (value: number) => Math.ceil(value / lotSize.value) * lotSize.value
const roundDownToLotSize = (value: number) => Math.floor(value / lotSize.value) * lotSize.value
const minUnits = computed(() => {
    const minimumUnits = bondListing.value?.instrument?.minUnitsToSell || 1
    return roundUpToLotSize(minimumUnits)
})
const maxUnits = computed(() => {
    const maximumUnits = bondListing.value?.unitsToSell || 0
    return maximumUnits > 0 ? roundDownToLotSize(maximumUnits) : 0
})
const hasAvailableUnitsLimit = computed(() => (bondListing.value?.unitsToSell || 0) > 0)
const emptyStateTitle = computed(() => isin.value ? 'Unable to load bond details' : 'Invalid bond link')
const investButtonLabel = computed(() => availableForSale.value ? 'Invest Securely' : "I'm interested")
const segmentIdResolved = computed(() => {
    const listing = bondListing.value as any
    return String(listing?.segmentId || listing?.id || listing?.externalListingId || '').trim()
})
const buildInvestorBondUrl = (ctaName: string, suffix = '') => {
    const segmentId = segmentIdResolved.value
    const baseUrl = segmentId
        ? investorUrl(`/bonds/${encodeURIComponent(segmentId)}${suffix}`)
        : investorUrl('/explore-products?tab=bonds')

    return buildRedirectUrl(baseUrl, route.path, ctaName)
}
const bondGateSignupUrl = computed(() =>
    buildInvestorBondUrl('signup-bond-gate')
)
const bondCashflowSignupUrl = computed(() => {
    const units = clampUnits(sharedUnits.value)
    return buildInvestorBondUrl(
        'signup-cashflow-bond-details',
        `?open=cashflow&units=${encodeURIComponent(String(units))}`
    )
})
const signupSectionTargets: Record<string, string> = {
    'signup-issuer-glance-bond-details': 'issuer-glance',
    'signup-strength-risk-bond-details': 'strengths-risks',
    'signup-credit-rating-bond-details': 'credit-rating',
    'signup-regulatory-assurance-bond-details': 'regulatory-assurance'
}

const clampUnits = (value: number) => {
    let units = Number(value)
    if (Number.isNaN(units)) units = minUnits.value

    units = Math.floor(units)
    units = roundDownToLotSize(units)

    if (units < minUnits.value) units = minUnits.value
    if (hasAvailableUnitsLimit.value && maxUnits.value > 0 && units > maxUnits.value) units = maxUnits.value

    return units
}

const investEquirizeUrl = computed(() => {
    const units = clampUnits(sharedUnits.value)
    return buildInvestorBondUrl(
        'invest-securely-bond-details',
        `/payment?units=${encodeURIComponent(String(units))}`
    )
})

const bondV2Data = computed(() => mapBondListingToBondV2Data(
    bondListing.value,
    tradeDate.value,
    sharedUnits.value,
    issuerFactsData.value,
    {
        useLegacyIssuerAboutFallback: !availableForSale.value
    }
))
const detailRegionStyle = computed(() => (
    stickyHeaderHeight.value > 0 ? { paddingTop: `${stickyHeaderHeight.value}px` } : {}
))

const toRecommendationNumber = (val: number | string | null | undefined): number => {
    if (val == null || val === '') return Number.NaN
    const n = typeof val === 'string' ? parseFloat(val) : Number(val)
    return Number.isFinite(n) ? n : Number.NaN
}

const formatRecommendationCurrency = (value: number): string => {
    if (!Number.isFinite(value) || value <= 0) return 'N/A'
    return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const getListingStableId = (listing: NormalizedBondListing): string => (
    String(listing.segmentId || listing.id || listing.instrument?.isin || '').trim()
)

const extractListingCandidates = (response: any): NormalizedBondListing[] => {
    let rawList: any[] = []
    if (Array.isArray(response)) {
        rawList = response
    } else if (Array.isArray(response?.listings)) {
        rawList = response.listings
    }

    return rawList.map((item: any) => normalizeBondListing(item as any))
}

const sortByYieldDistance = (list: NormalizedBondListing[], currentYield: number): NormalizedBondListing[] => {
    if (!Number.isFinite(currentYield)) return [...list]

    return [...list].sort((a, b) => {
        const aYield = toRecommendationNumber(a.listedYield)
        const bYield = toRecommendationNumber(b.listedYield)
        const aDiff = Number.isFinite(aYield) ? Math.abs(aYield - currentYield) : Number.POSITIVE_INFINITY
        const bDiff = Number.isFinite(bYield) ? Math.abs(bYield - currentYield) : Number.POSITIVE_INFINITY
        if (aDiff !== bDiff) return aDiff - bDiff

        const aDate = new Date(a.instrument?.maturityDate || '').getTime()
        const bDate = new Date(b.instrument?.maturityDate || '').getTime()
        const aTime = Number.isFinite(aDate) ? aDate : Number.POSITIVE_INFINITY
        const bTime = Number.isFinite(bDate) ? bDate : Number.POSITIVE_INFINITY
        if (aTime !== bTime) return aTime - bTime

        return getListingStableId(a).localeCompare(getListingStableId(b))
    })
}

const mapListingToRecommendationCard = (listing: NormalizedBondListing): BondProductCardProps => {
    const yieldNumber = toRecommendationNumber(listing.listedYield)
    const minInvestment = getBondMinInvestmentAmount(listing as any)
    const issuerName = listing.instrument?.issuerName || listing.instrument?.name || '—'
    const name = listing.instrument?.name || issuerName

    return {
        issuerName,
        name,
        issuerLogo: listing.instrument?.logoUrl,
        rating: listing.instrument?.creditRating || '',
        creditRiskMeter: listing.creditRiskMeter || (listing as any).credit_risk_meter || listing.instrument?.creditRiskMeter || (listing.instrument as any)?.credit_risk_meter || undefined,
        category: listing.instrument?.category || '',
        yieldRate: Number.isFinite(yieldNumber) ? yieldNumber.toFixed(2) : String(listing.listedYield || '—'),
        yieldLabel: 'Yield to Maturity',
        minAmount: formatRecommendationCurrency(minInvestment),
        payouts: listing.instrument?.couponFrequency || '—',
        maturityDate: listing.instrument?.maturityDate || '—',
        tags: listing.tags || [],
        bondId: String(listing.segmentId || listing.id || ''),
        isin: listing.instrument?.isin || ''
    }
}

const pickRecommendationListings = (
    currentListing: BondsListingDetail,
    listings: NormalizedBondListing[]
): NormalizedBondListing[] => {
    const currentIsin = String(currentListing.instrument?.isin || '').trim().toUpperCase()
    const currentRatingIndex = getCreditRatingIndex(currentListing.instrument?.creditRating)
    const betterRating = currentRatingIndex > 0 ? CREDIT_RATING_ORDER[currentRatingIndex - 1] : ''
    const worseRating = currentRatingIndex >= 0 && currentRatingIndex < CREDIT_RATING_ORDER.length - 1
        ? CREDIT_RATING_ORDER[currentRatingIndex + 1]
        : ''
    const currentYield = toRecommendationNumber(currentListing.listedYield)

    const filtered = listings.filter((listing) => {
        const listingIsin = String(listing.instrument?.isin || '').trim().toUpperCase()
        if (!listingIsin || listingIsin === currentIsin) return false
        return Boolean(getListingStableId(listing))
    })

    const betterMatches = betterRating
        ? filtered.filter((listing) => getPrimaryCreditRatingToken(listing.instrument?.creditRating) === betterRating)
        : []
    const worseMatches = worseRating
        ? filtered.filter((listing) => getPrimaryCreditRatingToken(listing.instrument?.creditRating) === worseRating)
        : []

    const betterSorted = sortByYieldDistance(betterMatches, currentYield)
    const worseSorted = sortByYieldDistance(worseMatches, currentYield)
    const selectedRatingMatches = [...betterSorted, ...worseSorted]
    const selectedIds = new Set(selectedRatingMatches.map((listing) => getListingStableId(listing)))
    const remaining = filtered.filter((listing) => !selectedIds.has(getListingStableId(listing)))
    const fallbackSorted = sortByYieldDistance(remaining, currentYield)

    return [...selectedRatingMatches, ...fallbackSorted].slice(0, RECOMMENDATION_COUNT)
}

const recommendationDataKey = computed(() => `bond-recommendations-${segmentIdResolved.value || slug.value}`)
const { data: recommendationCards, pending: recommendationsPending } = await useAsyncData(
    recommendationDataKey,
    async (): Promise<BondProductCardProps[]> => {
        const listing = bondListing.value
        if (!listing || availableForSale.value) return []

        try {
            const response = await bondsService.getBondsListings({
                page: 1,
                limit: RECOMMENDATION_FETCH_LIMIT,
                sortBy: 'listed_yield'
            })
            const candidates = extractListingCandidates(response)
            return pickRecommendationListings(listing, candidates).map(mapListingToRecommendationCard)
        } catch {
            return []
        }
    },
    {
        server: true,
        default: () => []
    }
)

provideBondV2Data(bondV2Data)

onBeforeRouteLeave(() => {
    if (lastLoadedBondDetailsData.value?.listing) {
        isLeavingPage.value = true
    }
})

const resetStickyState = () => {
    isBondHeaderFixed.value = false
    isBondSidebarFixed.value = false
    stickyHeaderHeight.value = 0
    bondHeaderStyle.value = {}
    bondSidebarStyle.value = {}
}

const updateStickyState = () => {
    stickyFrame = 0
    if (typeof window === 'undefined') return

    const scope = stickyScopeRef.value
    const header = stickyHeaderRef.value
    const detailRegion = detailRegionRef.value
    const stickyEnd = stickyEndRef.value
    const sidebar = sidebarRef.value
    const sidebarSticky = sidebarStickyRef.value

    if (!scope || !header || !detailRegion || !stickyEnd || !sidebar || !sidebarSticky || window.innerWidth <= 1080) {
        resetStickyState()
        return
    }

    const scrollY = window.scrollY
    const scopeTop = scope.getBoundingClientRect().top + scrollY
    const stickyEndTop = stickyEnd.getBoundingClientRect().top + scrollY
    const headerHeight = header.offsetHeight
    const navBottom = document.querySelector('nav')?.getBoundingClientRect().bottom ?? 0
    const headerTop = Math.max(0, Math.round(navBottom))
    const headerShouldFix = scrollY + headerTop >= scopeTop && scrollY + headerTop + headerHeight < stickyEndTop

    if (!headerShouldFix) {
        resetStickyState()
        return
    }

    const scopeRect = scope.getBoundingClientRect()
    isBondHeaderFixed.value = true
    stickyHeaderHeight.value = headerHeight
    bondHeaderStyle.value = {
        top: `${headerTop}px`,
        left: `${scopeRect.left}px`,
        width: `${scopeRect.width}px`
    }

    const sidebarTopDocument = sidebar.getBoundingClientRect().top + scrollY
    const detailRegionBottomDocument = detailRegion.getBoundingClientRect().bottom + scrollY
    const sidebarHeight = sidebarSticky.offsetHeight
    const preferredSidebarTop = headerTop + headerHeight + 16
    const maxSidebarOffset = Math.max(0, detailRegionBottomDocument - sidebarHeight - sidebarTopDocument)
    const desiredSidebarOffset = scrollY + preferredSidebarTop - sidebarTopDocument

    if (desiredSidebarOffset <= 0 || maxSidebarOffset <= 0) {
        isBondSidebarFixed.value = false
        bondSidebarStyle.value = {}
        return
    }

    if (desiredSidebarOffset >= maxSidebarOffset) {
        isBondSidebarFixed.value = false
        bondSidebarStyle.value = {
            transform: `translate3d(0, ${Math.round(maxSidebarOffset)}px, 0)`
        }
        return
    }

    isBondSidebarFixed.value = true
    bondSidebarStyle.value = {
        top: `${preferredSidebarTop}px`,
        left: `${sidebar.getBoundingClientRect().left}px`,
        width: `${sidebar.getBoundingClientRect().width}px`
    }
}

const scheduleStickyUpdate = () => {
    if (typeof window === 'undefined' || stickyFrame) return
    stickyFrame = window.requestAnimationFrame(updateStickyState)
}

const scheduleStickyRefresh = () => {
    if (typeof window === 'undefined') return
    scheduleStickyUpdate()
    activeTimeouts.value.push(
        globalThis.setTimeout(scheduleStickyUpdate, 80),
        globalThis.setTimeout(scheduleStickyUpdate, 220),
        globalThis.setTimeout(scheduleStickyUpdate, 420)
    )
}

const initializeStickyObservers = () => {
    if (typeof window === 'undefined') return

    stickyResizeObserver?.disconnect()
    if (!window.ResizeObserver) return

    lastHeights = new WeakMap<Element, number>()
    stickyResizeObserver = new ResizeObserver((entries) => {
        let hasSignificantChange = false
        for (const entry of entries) {
            const lastHeight = lastHeights.get(entry.target)
            const currentHeight = entry.contentRect.height
            
            if (lastHeight === undefined) {
                lastHeights.set(entry.target, currentHeight)
                hasSignificantChange = true
            } else {
                const diff = Math.abs(currentHeight - lastHeight)
                if (diff > 2) {
                    lastHeights.set(entry.target, currentHeight)
                    hasSignificantChange = true
                }
            }
        }
        if (hasSignificantChange) {
            scheduleStickyUpdate()
        }
    })

    ;[
        stickyScopeRef.value,
        stickyHeaderRef.value,
        detailRegionRef.value,
        sidebarRef.value,
        sidebarStickyRef.value,
        stickyEndRef.value
    ].forEach((element) => {
        if (element) stickyResizeObserver?.observe(element)
    })
}

const loadBondDetails = async () => {
    await refresh()
    await nextTick()
    initializeStickyObservers()
    scheduleStickyRefresh()
}

onMounted(async () => {
    window.addEventListener('scroll', scheduleStickyUpdate, { passive: true })
    window.addEventListener('resize', scheduleStickyUpdate)
    await nextTick()
    initializeStickyObservers()
    scheduleStickyRefresh()
    resetAfterCooldown()
    incrementVisit(slug.value || 'bonds')
    setupBroadcast()
    startPopupTimer(() => openClosablePopup())
})

onBeforeUnmount(() => {
    if (typeof window !== 'undefined' && stickyFrame) window.cancelAnimationFrame(stickyFrame)
    stickyResizeObserver?.disconnect()
    for (const timeout of activeTimeouts.value) {
        globalThis.clearTimeout(timeout)
    }
    activeTimeouts.value = []
    window.removeEventListener('scroll', scheduleStickyUpdate)
    window.removeEventListener('resize', scheduleStickyUpdate)
})

watch(bondListing, (listing) => {
    if (!listing) return
    sharedUnits.value = minUnits.value
    nextTick(scheduleStickyRefresh)
}, { immediate: true })

watch(recommendationCards, () => {
    nextTick(scheduleStickyRefresh)
})

const handleUnitsChanged = (units: number) => {
    sharedUnits.value = clampUnits(units)
}

const handleSignupRequired = (content: SignupDialogContent = defaultCashflowSignupContent) => {
    analytics.track(EVENTS.AUTH_REDIRECT_INITIATED, {
        cta_name:  content.ctaName,
        user_type: 'anonymous',
        page_type: 'onboarding',
        product:   'bonds',
        isin:      isin.value,
        issuer_name: bondListing.value?.instrument?.issuerName || formattedBondName.value,
    })

    if (content.ctaName === 'signup-cashflow-bond-details' && typeof window !== 'undefined') {
        window.location.assign(bondCashflowSignupUrl.value)
        return
    }

    const sectionTarget = signupSectionTargets[content.ctaName]
    if (sectionTarget && typeof globalThis !== 'undefined') {
        globalThis.location.assign(buildInvestorBondUrl(
            content.ctaName,
            `?open=${encodeURIComponent(sectionTarget)}`
        ))
        return
    }

    signupDialogContent.value = content
    showSignupDialog.value = true
}

const handleViewCashflow = (units?: number) => {
    if (typeof units === 'number') {
        sharedUnits.value = clampUnits(units)
    }

    handleSignupRequired(defaultCashflowSignupContent)
}

const interestInvestmentAmountValue = computed(() =>
    Number(interestInvestmentAmount.value)
)

const interestInvestmentAmountDisplay = computed(() => {
    if (!interestInvestmentAmount.value) return ''
    return `₹${Number(interestInvestmentAmount.value).toLocaleString('en-IN')}`
})

const canSubmitInterest = computed(() => {
    const nameOk = interestName.value.trim().length > 0
    const phoneOk = IN_MOBILE_10.test(interestPhoneDigits.value.trim())
    const amountOk = interestInvestmentAmountValue.value > 0
    return nameOk && phoneOk && amountOk
})

const onInterestPhoneInput = (event: Event) => {
    const target = event.target as HTMLInputElement
    interestPhoneDigits.value = target.value.replace(/\D/g, '').slice(0, 10)
    target.value = interestPhoneDigits.value
}

const onInterestInvestmentAmountInput = (event: Event) => {
    const target = event.target as HTMLInputElement
    const digits = target.value.replace(/\D/g, '').replace(/^0+/, '').slice(0, 12)
    interestInvestmentAmount.value = digits
    target.value = interestInvestmentAmountDisplay.value
}

const closeInterestModal = () => {
    showInterestModal.value = false
}

const focusableDialogSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
].join(',')

const getInterestModalFocusableElements = () => Array.from(
    interestModalDialogRef.value?.querySelectorAll<HTMLElement>(focusableDialogSelector) ?? []
).filter((element) => !element.hasAttribute('disabled') && element.offsetParent !== null)

const handleInterestModalKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
        event.preventDefault()
        closeInterestModal()
        return
    }

    if (event.key !== 'Tab') return

    const focusableElements = getInterestModalFocusableElements()
    if (!focusableElements.length) {
        event.preventDefault()
        interestModalDialogRef.value?.focus()
        return
    }

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
    } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
    }
}

const handleInterestClick = () => {
    interestModalTriggerElement = document.activeElement instanceof HTMLElement ? document.activeElement : null
    interestError.value = ''
    interestName.value = ''
    interestPhoneDigits.value = ''
    interestInvestmentAmount.value = ''
    showInterestModal.value = true
}

const handleInterestSubmit = async () => {
    if (typeof window === 'undefined') return

    const name = interestName.value.trim()
    const digits = interestPhoneDigits.value.trim()
    const investmentAmount = interestInvestmentAmountValue.value
    const isinVal = bondListing.value?.instrument?.isin || isin.value

    if (!canSubmitInterest.value) {
        if (!IN_MOBILE_10.test(interestPhoneDigits.value.trim())) {
            interestError.value = 'Enter a 10-digit mobile number (e.g., 9876543210)'
        } else {
            interestError.value = 'Please enter your name, a valid 10-digit WhatsApp number, and investment amount.'
        }
        return
    }

    if (!isinVal) {
        interestError.value = 'Unable to submit: bond details missing.'
        return
    }

    interestError.value = ''
    interestSubmitting.value = true

    try {
        const listingNameVal = bondListing.value?.instrument?.issuerName || bondListing.value?.instrument?.name || formattedBondName.value || ''
        await bondsService.submitLead(name, digits, isinVal, listingNameVal, investmentAmount)
        closeInterestModal()
        const message = `Hi, I am interested in buying ${isinVal} - ${listingNameVal} - ${name}.`
        const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`
        window.open(whatsappUrl, '_blank')
    } catch (error: any) {
        interestError.value = error?.data?.message || error?.message || 'Something went wrong. Please try again.'
    } finally {
        interestSubmitting.value = false
    }
}

watch(showInterestModal, async (isOpen) => {
    if (typeof document === 'undefined') return

    if (isOpen) {
        await nextTick()
        interestModalDialogRef.value?.focus()
        return
    }

    await nextTick()
    interestModalTriggerElement?.focus()
    interestModalTriggerElement = null
})

const handleInvest = async () => {
    if (typeof window === 'undefined') return

    if (!availableForSale.value) {
        handleInterestClick()
    } else {
        analytics.track(EVENTS.AUTH_REDIRECT_INITIATED, {
            cta_name:  'invest-securely',
            user_type: 'anonymous',
            page_type: 'onboarding',
            product:   'bonds',
            isin:      isin.value,
            issuer_name: bondListing.value?.instrument?.issuerName || formattedBondName.value,
        })
        window.location.assign(investEquirizeUrl.value)
    }

    investPanelRef.value?.resetLoading()
}

const goBack = () => {
    if (lastLoadedBondDetailsData.value?.listing) {
        isLeavingPage.value = true
    }
    router.back()
}

const canonicalUrl = computed(() => {
    const path = getCanonicalBondPath(bondListing.value)
    return path ? `${SITE_URL}${path}` : `${SITE_URL}/products/bonds/${slug.value}`
})

const seoMeta = computed(() => (bondListing.value as any)?.metadata || null)

const bondPageTitle = computed(() => {
    if (!bondListing.value) return 'Bond Details | Equirize'
    const issuerName = bondListing.value.instrument?.issuerName || bondListing.value.instrument?.name || formattedBondName.value
    const yieldValue = bondListing.value.listedYield || bondListing.value.instrument?.couponRate || ''
    const numericYield = parseFloat(String(yieldValue))
    const formattedYield = Number.isFinite(numericYield) ? `${numericYield.toFixed(2)}%` : ''
    const titleParts = [isin.value, formattedYield, issuerName, 'Equirize'].filter(Boolean)

    return titleParts.join(' | ')
})

const formatSeoDate = (value?: string) => {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
}

const bondSeoDescription = computed(() => {
    if (seoMeta.value?.description) return seoMeta.value.description
    if (!bondListing.value) {
        return 'View detailed bond information including yield, maturity date, credit rating, and investment details on Equirize.'
    }

    const issuerName = bondListing.value.instrument?.issuerName || formattedBondName.value || 'this bond'
    const isinVal = bondListing.value.instrument?.isin || isin.value || ''
    const yieldRate = bondListing.value.listedYield || bondListing.value.instrument?.couponRate || ''
    const maturityDate = bondListing.value.instrument?.maturityDate || ''
    const rating = bondListing.value.instrument?.creditRating || ''
    const ratingAgency = bondListing.value.instrument?.creditRatingAgency || ''
    let description = `Invest in ${issuerName}${isinVal ? ` ${isinVal}` : ''}`

    if (yieldRate) description += ` with ${parseFloat(String(yieldRate)).toFixed(2)}% yield/coupon`
    if (rating && ratingAgency) description += `, ${ratingAgency} rated ${rating}`
    else if (rating) description += `, rated ${rating}`
    if (maturityDate) description += `. Maturity date: ${formatSeoDate(maturityDate)}`
    description += '. View complete bond details, cashflow schedule, issuer facts and investment information on Equirize.'

    return description
})

const bondSeoKeywords = computed(() => {
    if (seoMeta.value?.keywords) return seoMeta.value.keywords
    if (!bondListing.value) return 'bonds, bond investment, fixed income, debt securities, Equirize'

    const keywords: string[] = []
    const issuerName = bondListing.value.instrument?.issuerName || ''
    const isinVal = bondListing.value.instrument?.isin || isin.value || ''
    const category = bondListing.value.instrument?.category || ''
    const rating = bondListing.value.instrument?.creditRating || ''

    if (issuerName) keywords.push(issuerName, `${issuerName} bonds`)
    if (isinVal) keywords.push(`ISIN ${isinVal}`, isinVal)
    if (category) keywords.push(category)
    if (rating) keywords.push(`${rating} rated bonds`)
    keywords.push('bonds', 'bond investment', 'fixed income', 'debt securities', 'listed bonds', 'Equirize')

    return keywords.join(', ')
})

useHead({
    htmlAttrs: {
        lang: 'en'
    },
    title: bondPageTitle,
    meta: [
        {
            name: 'description',
            content: bondSeoDescription
        },
        {
            name: 'keywords',
            content: bondSeoKeywords
        },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: canonicalUrl },
        { property: 'og:title', content: bondPageTitle },
        {
            property: 'og:description',
            content: bondSeoDescription
        },
        { property: 'og:image', content: `${SITE_URL}/images/hero-bondBanner-desktop.webp` },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: bondPageTitle },
        { name: 'twitter:description', content: bondSeoDescription },
        { name: 'twitter:image', content: `${SITE_URL}/images/hero-bondBanner-desktop.webp` }
    ],
    link: [{ rel: 'canonical', href: canonicalUrl }]
})
</script>

<style scoped>
.bond-v2-page {
    --bond-v2-border: #eaf7ff;
    --bond-v2-border-strong: #d5efff;
    --bond-v2-surface: #ffffff;
    --bond-v2-muted: #fcfdff;
    --bond-v2-soft: #f7fbff;
    --bond-v2-primary: #0027af;
    --bond-v2-primary-dark: #001a78;
    --bond-v2-text: #1b2559;
    --bond-v2-secondary: #424d71;
    --bond-v2-radius: 7px;
    --bond-v2-inner-radius: 4px;
    --bond-v2-section-gap: 16px;
    --bond-v2-topbar-height: 87px;
    --bond-v2-sticky-header-height: 167px;
    min-height: 100vh;
    background: #fff;
}

.bond-v2-skip-link {
    position: fixed;
    top: 12px;
    left: 12px;
    z-index: 10000;
    transform: translateY(-160%);
    background: #0027af;
    color: #fff;
    border-radius: 6px;
    padding: 10px 14px;
    font-family: var(--font-family-primary, 'Inter', sans-serif);
    font-size: 14px;
    font-weight: 800;
    text-decoration: none;
}

.bond-v2-skip-link:focus,
.bond-v2-skip-link:focus-visible {
    transform: translateY(0);
    outline: 3px solid #ffd700;
    outline-offset: 2px;
}

.bond-v2-page :focus-visible {
    outline: 3px solid #0027af;
    outline-offset: 3px;
}

.bond-v2-main:focus,
.bond-v2-main:focus-visible {
    outline: none;
}

.bond-v2-sticky-header {
    background: #fff;
}

.bond-v2-sticky-header--fixed {
    position: fixed;
    z-index: 120;
}

.bond-v2-sticky-scope {
    position: relative;
}

.bond-v2-sticky-end {
    width: 100%;
    height: 1px;
}

.bond-v2-page__container {
    max-width: 1440px;
    margin: 0 auto;
    padding: 0 24px 40px;
    box-sizing: border-box;
}

.bond-v2-back {
    height: 72px;
    display: flex;
    align-items: center;
}

.bond-v2-back button {
    width: 48px;
    height: 48px;
    border: none;
    border-radius: 0;
    background: transparent;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.bond-v2-back img {
    width: 24px;
    height: 24px;
}

.bond-v2-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 350px;
    gap: 37px;
    align-items: start;
    margin-top: 22px;
}

.bond-v2-main {
    display: flex;
    flex-direction: column;
    gap: var(--bond-v2-section-gap);
}

.bond-v2-overview-box {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    gap: 24px;
    width: 100%;
    background: #F9FBFF;
    border: none;
    border-radius: 7px;
    margin-top: 16px;
    padding: 12px;
}

.bond-v2-header-skeleton {
    width: 100vw;
    height: 95px;
    margin-left: calc(50% - 50vw);
    background: linear-gradient(180deg, #fff 25.96%, #fffbe0 100%);
    position: relative;
    display: block;
    padding: 0;
    box-sizing: border-box;
}

.bond-v2-header-skeleton::before {
    content: "";
    display: block;
    max-width: 1440px;
    height: 100%;
    margin: 0 auto;
}

.bond-v2-header-skeleton__issuer {
    width: min(calc(100% - 48px), 1392px);
    max-width: 1392px;
    position: absolute;
    left: max(24px, calc((100vw - 1440px) / 2 + 24px));
    top: 39px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.bond-v2-header-skeleton__copy {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.bond-v2-skeleton-shimmer {
    position: relative;
    overflow: hidden;
    background: linear-gradient(90deg, #eef2f7 25%, #e3e8f0 50%, #eef2f7 75%);
    background-size: 200% 100%;
    animation: bond-v2-shimmer 1.5s infinite;
}

.bond-v2-skeleton-chip {
    position: absolute;
    left: max(24px, calc((100vw - 1440px) / 2 + 24px));
    top: 0;
    width: 132px;
    height: 26px;
    border-radius: 0 0 4px 4px;
}

.bond-v2-skeleton-chip--short {
    left: max(164px, calc((100vw - 1440px) / 2 + 164px));
    width: 44px;
}

.bond-v2-skeleton-chip--right {
    left: auto;
    right: max(24px, calc((100vw - 1440px) / 2 + 24px));
    width: 176px;
}

.bond-v2-skeleton-logo {
    width: 24px;
    height: 24px;
    border-radius: 3px;
    flex: 0 0 auto;
}

.bond-v2-skeleton-line {
    height: 12px;
    border-radius: 999px;
}

.bond-v2-skeleton-line--title {
    width: 260px;
    height: 18px;
}

.bond-v2-skeleton-line--small {
    width: 132px;
}

.bond-v2-skeleton-line--metric {
    width: 100px;
    height: 16px;
}

.bond-v2-skeleton-line--label {
    width: 82px;
}

.bond-v2-skeleton-line--wide {
    width: 100%;
    height: 18px;
}

.bond-v2-skeleton-line--section {
    width: 160px;
    height: 16px;
}

.bond-v2-skeleton-line--center {
    width: 64px;
    height: 16px;
    margin: 4px auto 8px;
}

.bond-v2-overview-box--skeleton {
    gap: 16px;
}

.bond-v2-metrics-skeleton {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
}

.bond-v2-metric-skeleton-card {
    min-height: 116px;
    border-radius: 7px;
    background: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 16px 12px;
}

.bond-v2-skeleton-icon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
}

.bond-v2-overview-summary-skeleton {
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: #fff;
    border-radius: 7px;
    padding: 18px 20px;
}

.bond-v2-yield-skeleton-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
}

.bond-v2-yield-skeleton-card {
    height: 84px;
    border-radius: 7px;
}

.bond-v2-cashflow-skeleton,
.bond-v2-invest-skeleton {
    border: none;
    border-radius: 7px;
    background: #F9FBFF;
    padding: 12px;
}

.bond-v2-cashflow-skeleton {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.bond-v2-cashflow-summary-skeleton {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
}

.bond-v2-cashflow-summary-card {
    height: 64px;
    border-radius: 4px;
}

.bond-v2-table-skeleton,
.bond-v2-invest-skeleton__totals {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.bond-v2-table-skeleton__row {
    height: 40px;
    border-radius: 4px;
}

.bond-v2-invest-skeleton {
    width: 350px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.bond-v2-invest-skeleton__unit {
    height: 53px;
    border-radius: 5px;
}

.bond-v2-invest-skeleton__buttons {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
}

.bond-v2-invest-skeleton__button {
    height: 26px;
    border-radius: 4px;
}

.bond-v2-documents-skeleton {
    width: 100%;
    background: #fff;
    border: 1px solid #eaf7ff;
    border-radius: 8px;
    padding: 17px 20px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.bond-v2-skeleton-line--docs-title {
    width: 190px;
    height: 18px;
}

.bond-v2-documents-skeleton__rows {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.bond-v2-documents-skeleton__row {
    min-height: 39px;
    padding: 8px 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    box-sizing: border-box;
}

.bond-v2-documents-skeleton__row:nth-child(even) {
    background: #f9fbff;
}

.bond-v2-skeleton-line--docs-row {
    width: min(190px, 75%);
    height: 16px;
}

.bond-v2-skeleton-doc-icon {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    flex: 0 0 auto;
}

.bond-v2-empty-state {
    min-height: 420px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 36px 24px 72px;
    box-sizing: border-box;
}

.bond-v2-empty-state__content {
    width: min(100%, 520px);
    border-radius: 7px;
    background: linear-gradient(180deg, #f9fbff 0%, #ffffff 100%);
    padding: 36px 32px;
    text-align: center;
    box-sizing: border-box;
    box-shadow: inset 0 0 0 1px #eaf7ff;
}

.bond-v2-empty-state__icon {
    width: 56px;
    height: 56px;
    margin: 0 auto 16px;
    border-radius: 50%;
    background: #eaf7ff;
    color: #0027af;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.bond-v2-empty-state__title {
    margin: 0;
    color: #1b2559;
    font-family: var(--font-family-primary, 'Inter', sans-serif);
    font-size: 20px;
    font-weight: 700;
    line-height: 28px;
}

.bond-v2-empty-state__actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-top: 22px;
    flex-wrap: wrap;
}

.bond-v2-empty-state__button {
    min-width: 124px;
}

.bond-v2-empty-state__link {
    min-width: 104px;
    min-height: 46px;
    border: 1px solid #d5efff;
    border-radius: 6px;
    background: #ffffff;
    color: #1b2559;
    font-family: var(--font-family-primary, 'Inter', sans-serif);
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
}

.bond-v2-empty-state__link:hover {
    background: #eaf7ff;
}

.interest-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 16px;
}

.interest-modal {
    background: var(--color-surface-main, #fff);
    border-radius: 12px;
    padding: 24px;
    max-width: 400px;
    width: 100%;
    position: relative;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.interest-modal__title {
    margin: 0 0 8px;
    font-size: 18px;
    font-weight: 700;
    color: #1b2559;
}

.interest-modal__bond-label {
    margin: 0 0 20px;
    font-size: 13px;
    color: #5a6a8a;
    background: #eaf7ff;
    padding: 8px 12px;
    border-radius: 8px;
}

.interest-modal__form {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.interest-modal__field label {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #1b2559;
    margin-bottom: 6px;
}

.interest-modal__input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #e0e5ef;
    border-radius: 8px;
    font-size: 14px;
    box-sizing: border-box;
}

.interest-modal__input:focus {
    outline: none;
    border-color: #0027af;
}

.interest-modal__phone-row {
    display: flex;
    align-items: stretch;
    border: 1px solid #e0e5ef;
    border-radius: 8px;
    overflow: hidden;
    box-sizing: border-box;
}

.interest-modal__phone-prefix {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding: 10px 8px 10px 12px;
    font-size: 14px;
    font-weight: 600;
    color: #1b2559;
    background: #f4f7fb;
    border-right: 1px solid #e0e5ef;
}

.interest-modal__input--phone {
    flex: 1;
    min-width: 0;
    border: none;
    border-radius: 0;
}

.interest-modal__input--phone:focus {
    outline: none;
}

.interest-modal__phone-row:focus-within {
    border-color: #0027af;
}

.interest-modal__instruction {
    margin: 0;
    font-size: 13px;
    color: #5b6684;
    font-weight: 500;
}

.interest-modal__error {
    margin: 0;
    font-size: 13px;
    color: #c0392b;
}

.interest-modal__submit {
    padding: 12px 20px;
    background: #0027af;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
}

.interest-modal__submit:hover:not(:disabled) {
    background: #001d82;
}

.interest-modal__submit:disabled {
    background: #d9d9d9 !important;
    color: #1f1f1f !important;
    opacity: 1;
    cursor: not-allowed;
}

.interest-modal__close {
    position: absolute;
    top: 12px;
    right: 12px;
    background: none;
    border: none;
    font-size: 24px;
    line-height: 1;
    color: #5a6a8a;
    cursor: pointer;
    padding: 4px;
}

.interest-modal__close:hover {
    color: #1b2559;
}

@keyframes bond-v2-shimmer {
    0% {
        background-position: 200% 0;
    }

    100% {
        background-position: -200% 0;
    }
}

@media (prefers-reduced-motion: reduce) {
    .bond-v2-page *,
    .bond-v2-page *::before,
    .bond-v2-page *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        scroll-behavior: auto !important;
        transition-duration: 0.01ms !important;
    }
}

.bond-v2-sidebar {
    width: 350px;
    align-self: start;
    box-sizing: border-box;
}

.bond-v2-sidebar__sticky {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
    width: 100%;
}

.bond-v2-recommendations {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    margin-top: 0;
    padding: 0;
    box-sizing: border-box;
}

.bond-v2-recommendations__title {
    display: block;
    width: 100%;
    margin: 0 0 10px;
    padding: 0;
    position: static;
    color: #1b2559;
    font-family: var(--font-family-primary, 'Inter', sans-serif);
    font-size: 15px;
    line-height: 22px;
    font-weight: 700;
}

.bond-v2-recommendations__loading {
    margin: 0;
    color: #5b6684;
    font-size: 13px;
    line-height: 20px;
}

.bond-v2-recommendations__cards {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    gap: 14px;
}

.bond-v2-recommendations :deep(.grid-card) {
    width: 100%;
    max-width: 100%;
    min-height: 210px;
}

.bond-v2-recommendations :deep(.card-content) {
    min-width: 0;
    padding: 17px 14px;
    gap: 8px;
}

.bond-v2-recommendations :deep(.details-panel) {
    width: 100%;
    justify-content: space-between;
    gap: 6px;
    padding: 9px 8px;
    box-sizing: border-box;
}

.bond-v2-recommendations :deep(.detail-item) {
    min-width: 0;
    gap: 4px;
}

.bond-v2-recommendations :deep(.detail-item:not(:last-child)) {
    margin-right: 0;
}

.bond-v2-recommendations :deep(.detail-text) {
    min-width: 0;
    font-size: 11px;
    overflow: hidden;
    text-overflow: ellipsis;
}

.bond-v2-sidebar__sticky--fixed {
    position: fixed;
    z-index: 30;
    transform: translate3d(0, 0, 0);
    will-change: transform;
}

.bond-v2-section {
    margin-top: 42px;
}

.bond-v2-footer {
    margin-top: 48px;
}

@media (max-width: 1320px) {
    .bond-v2-page__container {
        padding-left: 24px;
        padding-right: 24px;
    }
}

@media (max-width: 1080px) {
    .bond-v2-layout {
        grid-template-columns: 1fr;
    }

    .bond-v2-sidebar {
        width: 100%;
    }

}

@media (max-width: 768px) {
    .bond-v2-page__container {
        padding-bottom: 112px;
    }
}

@media (max-width: 640px) {
    .bond-v2-page {
        --bond-v2-section-gap: 12px;
        overflow-x: hidden;
    }

    .bond-v2-page__container {
        max-width: 100%;
        padding-left: 0;
        padding-right: 0;
        padding-bottom: 112px;
        overflow-x: hidden;
    }

    .bond-v2-back {
        height: 42px;
        padding: 0 16px;
        box-sizing: border-box;
    }

    .bond-v2-back button {
        width: 36px;
        height: 36px;
        justify-content: flex-start;
    }

    .bond-v2-layout {
        gap: 12px;
        margin-top: 12px;
        width: 100%;
        overflow: hidden;
    }

    .bond-v2-main,
    .bond-v2-sidebar {
        width: 100%;
        min-width: 0;
    }

    .bond-v2-overview-box {
        gap: 12px;
        border-radius: 0;
        margin-top: 0;
        padding: 12px 16px;
        box-sizing: border-box;
    }

    .bond-v2-header-skeleton {
        width: 100%;
        height: auto;
        min-height: 76px;
        margin-left: 0;
        padding: 0 16px 8px;
        box-sizing: border-box;
        display: grid;
        grid-template-columns: 104px 42px minmax(0, 1fr) minmax(96px, 132px);
        grid-template-rows: 20px auto;
        column-gap: 4px;
        row-gap: 8px;
        align-items: start;
    }

    .bond-v2-header-skeleton::before {
        display: none;
    }

    .bond-v2-skeleton-chip,
    .bond-v2-skeleton-chip--short,
    .bond-v2-skeleton-chip--right {
        position: static;
        height: 20px;
        border-radius: 0 0 4px 4px;
    }

    .bond-v2-skeleton-chip {
        width: 104px;
        grid-column: 1;
        grid-row: 1;
    }

    .bond-v2-skeleton-chip--short {
        width: 42px;
        grid-column: 2;
        grid-row: 1;
    }

    .bond-v2-skeleton-chip--right {
        width: 100%;
        justify-self: end;
        grid-column: 4;
        grid-row: 1;
    }

    .bond-v2-header-skeleton__issuer {
        position: static;
        width: 100%;
        margin-top: 0;
        grid-column: 1 / -1;
        grid-row: 2;
        gap: 6px;
    }

    .bond-v2-skeleton-logo {
        width: 20px;
        height: 20px;
    }

    .bond-v2-skeleton-line--title {
        width: min(230px, calc(100vw - 78px));
        height: 14px;
    }

    .bond-v2-skeleton-line--small {
        width: 118px;
        height: 10px;
    }

    .bond-v2-metrics-skeleton {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px;
    }

    .bond-v2-metric-skeleton-card {
        min-height: 98px;
        padding: 12px 8px;
        gap: 8px;
    }

    .bond-v2-cashflow-summary-skeleton {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 6px;
    }

    .bond-v2-yield-skeleton-row {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 6px;
    }

    .bond-v2-yield-skeleton-card {
        height: 64px;
    }

    .bond-v2-cashflow-skeleton,
    .bond-v2-invest-skeleton,
    .bond-v2-documents-skeleton,
    .bond-v2-recommendations {
        border-radius: 0;
        padding: 12px 16px;
        box-sizing: border-box;
        width: 100%;
    }

    .bond-v2-invest-skeleton__buttons {
        gap: 8px;
    }

    .bond-v2-sidebar {
        gap: 12px;
    }

    .bond-v2-section {
        margin-top: 28px;
        padding: 0 16px;
        box-sizing: border-box;
    }

    .bond-v2-empty-state {
        min-height: 340px;
        padding: 28px 16px 48px;
    }

    .bond-v2-empty-state__content {
        padding: 28px 18px;
    }

    .bond-v2-empty-state__title {
        font-size: 17px;
        line-height: 26px;
    }

    .bond-v2-empty-state__actions {
        flex-direction: column;
        align-items: stretch;
    }

    .bond-v2-footer {
        margin-top: 32px;
        padding: 0 16px;
        box-sizing: border-box;
    }
}
</style>
